// Unified error handling system for all environments
export interface ApiError {
  statusCode: number;
  message: string;
  code?: string;
}

export interface ErrorResponse {
  statusCode: number;
  errors?: Array<{ message: string; code?: string }>;
  message?: string;
}

// Error type classification
export enum ErrorType {
  NETWORK = 'network',
  VALIDATION = 'validation',
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  NOT_FOUND = 'not_found',
  SERVER_ERROR = 'server_error',
  UNKNOWN = 'unknown'
}

// Friendly error messages for different error types
const ERROR_MESSAGES = {
  [ErrorType.NETWORK]: {
    title: 'Connection Error',
    message: 'Unable to connect to the server. Please check your internet connection and try again.'
  },
  [ErrorType.VALIDATION]: {
    title: 'Invalid Information',
    message: 'Please check the information you entered and try again.'
  },
  [ErrorType.AUTHENTICATION]: {
    title: 'Authentication Failed',
    message: 'Your login credentials are incorrect. Please try again.'
  },
  [ErrorType.AUTHORIZATION]: {
    title: 'Access Denied',
    message: 'You don\'t have permission to perform this action.'
  },
  [ErrorType.NOT_FOUND]: {
    title: 'Not Found',
    message: 'The requested resource was not found.'
  },
  [ErrorType.SERVER_ERROR]: {
    title: 'Server Error',
    message: 'Something went wrong on our end. Please try again later.'
  },
  [ErrorType.UNKNOWN]: {
    title: 'Unexpected Error',
    message: 'An unexpected error occurred. Please try again.'
  }
};

// HTTP status code to error type mapping
const STATUS_CODE_MAPPING: Record<number, ErrorType> = {
  400: ErrorType.VALIDATION,
  401: ErrorType.AUTHENTICATION,
  403: ErrorType.AUTHORIZATION,
  404: ErrorType.NOT_FOUND,
  422: ErrorType.VALIDATION,
  429: ErrorType.VALIDATION,
  500: ErrorType.SERVER_ERROR,
  502: ErrorType.SERVER_ERROR,
  503: ErrorType.SERVER_ERROR,
  504: ErrorType.NETWORK,
};

// Classify error based on status code or error response
export const classifyError = (error: any): ErrorType => {
  // Check if it's an API error response
  if (error.statusCode) {
    return STATUS_CODE_MAPPING[error.statusCode] || ErrorType.UNKNOWN;
  }
  
  // Check if it's an HTTP error
  if (error.status) {
    return STATUS_CODE_MAPPING[error.status] || ErrorType.UNKNOWN;
  }
  
  // Check for network errors
  if (error.message?.includes('Network') || error.message?.includes('fetch')) {
    return ErrorType.NETWORK;
  }
  
  // Check for validation errors
  if (error.message?.includes('validation') || error.message?.includes('invalid')) {
    return ErrorType.VALIDATION;
  }
  
  return ErrorType.UNKNOWN;
};

// Backend `Result.errors` is typed `any[]` and is inconsistent across the API — some call
// sites push `{ message: string }` objects, others push plain strings directly (e.g.
// `['A business with this email already exists...']`). Handle both shapes so a real,
// specific backend message never silently falls back to a generic one. Exported for direct
// use against a non-thrown `ApiResponse.errors` array (e.g. `response.errors?.[0]`), where
// handleApiError itself doesn't apply since nothing was thrown/caught.
export const extractErrorEntryMessage = (entry: any): string | undefined => {
  if (typeof entry === 'string') return entry;
  if (entry && typeof entry.message === 'string') return entry.message;
  return undefined;
};

// Get friendly error message
export const getFriendlyErrorMessage = (error: any): { title: string; message: string } => {
  const errorType = classifyError(error);
  const baseMessage = ERROR_MESSAGES[errorType];

  // If we have a specific error message from the API, use it
  const apiMessage = extractErrorEntryMessage(error.errors?.[0]);
  if (apiMessage) {
    return {
      title: baseMessage.title,
      message: apiMessage
    };
  }

  // If we have a direct message, use it
  if (error.message && error.message !== 'Network Error') {
    return {
      title: baseMessage.title,
      message: error.message
    };
  }
  
  // Return the base message for the error type
  return baseMessage;
};

// Handle API errors and return user-friendly messages
export const handleApiError = (error: any): { title: string; message: string; type: 'error' | 'warning' | 'info' } => {
  const { title, message } = getFriendlyErrorMessage(error);
  
  // Determine notification type based on error type
  const errorType = classifyError(error);
  let type: 'error' | 'warning' | 'info' = 'error';
  
  if (errorType === ErrorType.VALIDATION) {
    type = 'warning';
  } else if (errorType === ErrorType.NETWORK) {
    type = 'info';
  }
  
  return { title, message, type };
};

// Log error for debugging (only in development)
export const logError = (error: any, context?: string) => {
  if (__DEV__) {
    console.error(`[${context || 'Error'}]`, error);
  }
};
